import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useGetContractsQuery } from '../../api/contracts/contractApiSlice';
import type { ApiAssetInfo } from '../../api/tags/tag.types';
import { fetchAssets } from '../../api/tags/tagCalls';
import {
    BookingOverviewEmptyState,
    BookingOverviewErrorState,
    BookingOverviewForbiddenState,
} from './BookingOverviewStates';
import { BookingOverviewStats } from './BookingOverviewStats';
import { getBookingOverviewStatistics } from './bookingOverviewService';

export function BookingOverviewContainer() {
    const {
        data: contracts,
        error: contractsError,
        isError: hasContractsError,
        isFetching,
        refetch,
    } = useGetContractsQuery(undefined);
    const [assets, setAssets] = useState<ApiAssetInfo[]>();
    const [hasError, setHasError] = useState(false);
    const [reloadCount, setReloadCount] = useState(0);

    const reload = () => {
        setAssets(undefined);
        setHasError(false);
        setReloadCount(count => count + 1);
        refetch();
    };

    useEffect(() => {
        let isMounted = true;
        fetchAssets()
            .then(assets => {
                if (isMounted) {
                    setAssets(assets);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setHasError(true);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [reloadCount]);

    if (hasContractsError && !isFetching) {
        if (
            contractsError &&
            'status' in contractsError &&
            (contractsError.status === 401 || contractsError.status === 403)
        ) {
            return <BookingOverviewForbiddenState />;
        }

        return <BookingOverviewErrorState onReload={reload} />;
    }

    if (isFetching || !contracts) {
        return <Spinner text={<FormattedMessage id='intl-msg:marketplace.loading' />} isInverse={false} />;
    }

    if (contracts.length === 0) {
        return <BookingOverviewEmptyState />;
    }

    if (hasError) {
        return <BookingOverviewErrorState onReload={reload} />;
    }

    if (!assets) {
        return <Spinner text={<FormattedMessage id='intl-msg:marketplace.loading' />} isInverse={false} />;
    }

    return <BookingOverviewStats statistics={getBookingOverviewStatistics(contracts, assets)} />;
}
